import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { PosterPreview } from "@/components/posters/PosterPreview";

const NUM_KEYS = [["x", "X"], ["y", "Y"], ["width", "W"], ["height", "H"], ["max_font_size", "Max pt"], ["min_font_size", "Min pt"]];
const LABELS = { franchise: "Franchise heading", description: "Project description", name: "Contact name", email: "Email", phone: "Telephone" };
const cell = "w-full px-2 py-1 bg-white border border-stone-300 rounded text-xs tabular-nums focus:outline-none focus:border-stone-900 disabled:bg-stone-50 disabled:text-stone-500";

function LayoutRow({ f, i, locked, onChange }) {
  return (
    <tr className="border-t border-stone-100" data-testid={`layout-row-${f.key}`}>
      <td className="py-1.5 pr-2 text-xs font-semibold text-stone-800 whitespace-nowrap">{LABELS[f.key]}</td>
      {NUM_KEYS.map(([k]) => (
        <td key={k} className="py-1.5 pr-1.5 min-w-[4.2rem]">
          <input type="number" step="0.5" value={f[k]} disabled={locked} data-testid={`layout-${f.key}-${k}`}
            onChange={(e) => onChange(i, { [k]: e.target.value === "" ? "" : Number(e.target.value) })} className={cell} />
        </td>
      ))}
      <td className="py-1.5 pr-1.5">
        <select value={f.alignment} disabled={locked} onChange={(e) => onChange(i, { alignment: e.target.value })} data-testid={`layout-${f.key}-alignment`} className={cell}>
          <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option>
        </select>
      </td>
      <td className="py-1.5 pr-1.5">
        <select value={f.font} disabled={locked} onChange={(e) => onChange(i, { font: e.target.value })} data-testid={`layout-${f.key}-font`} className={cell}>
          <option value="sans-bold">Sans bold</option><option value="serif-regular">Serif regular</option>
        </select>
      </td>
      <td className="py-1.5 min-w-[5.5rem]">
        <input type="text" value={f.colour} disabled={locked} onChange={(e) => onChange(i, { colour: e.target.value })} data-testid={`layout-${f.key}-colour`} className={cell} />
      </td>
    </tr>
  );
}

export function AdminPosterTemplateEditor({ template, onSaved }) {
  const locked = template.status !== "draft";
  const [meta, setMeta] = useState({ title: template.title, description: template.description, default_description: template.default_description });
  const [layout, setLayout] = useState(template.layout);
  const [preview, setPreview] = useState(null);
  const [previewedKey, setPreviewedKey] = useState(null);
  const seq = useRef(0);
  const currentKey = JSON.stringify([layout, meta.default_description || ""]);
  const [busy, setBusy] = useState("");
  const change = (i, p) => setLayout((l) => ({ ...l, fields: l.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) }));

  const runPreview = async () => {
    const my = ++seq.current;
    const key = currentKey;
    const wording = meta.default_description || "";
    setBusy("preview");
    try {
      const { data } = await api.post(`/admin/poster-templates/${template.id}/sample-preview`, { layout, default_description: wording });
      if (my === seq.current) { setPreview({ ...data, wording }); setPreviewedKey(key); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { if (my === seq.current) setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const body = locked ? meta : { ...meta, layout };
      const { data } = await api.patch(`/admin/poster-templates/${template.id}`, body);
      toast.success("Template saved"); onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const input = "w-full px-3 py-2 bg-white border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-stone-900";
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-6 mt-4" data-testid={`poster-template-editor-${template.id}`}>
      <div className="space-y-3">
        <label className="block"><span className="block text-xs font-semibold text-stone-700 mb-1">Title</span>
          <input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className={input} data-testid="template-edit-title" /></label>
        <label className="block"><span className="block text-xs font-semibold text-stone-700 mb-1">Description (optional)</span>
          <input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className={input} data-testid="template-edit-description" /></label>
        <label className="block"><span className="block text-xs font-semibold text-stone-700 mb-1">Default project wording <span className="font-normal text-stone-500">(leave blank if the artwork already includes it)</span></span>
          <textarea rows={2} value={meta.default_description} onChange={(e) => setMeta({ ...meta, default_description: e.target.value })} className={input} data-testid="template-edit-default" /></label>
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-1">Text areas (PDF points, top-left origin)</div>
          {locked && <p className="text-[11px] text-amber-800 mb-1" data-testid="template-layout-locked">This version is {template.status}, so its layout is locked. Create a new version to change placement or typography.</p>}
          <div className="overflow-x-auto border border-stone-200 rounded-lg px-3 py-1 bg-white">
            <table className="w-full">
              <thead><tr className="text-[10px] uppercase tracking-wider text-stone-500 text-left">
                <th className="py-1.5 pr-2">Field</th>{NUM_KEYS.map(([k, l]) => <th key={k} className="py-1.5 pr-1.5">{l}</th>)}<th className="pr-1.5">Align</th><th className="pr-1.5">Font</th><th>Colour</th>
              </tr></thead>
              <tbody>{layout.fields.map((f, i) => <LayoutRow key={f.key} f={f} i={i} locked={locked} onChange={change} />)}</tbody>
            </table>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-stone-700 mt-2">Line height
            <input type="number" step="0.05" value={layout.line_height} disabled={locked} data-testid="layout-line-height"
              onChange={(e) => setLayout({ ...layout, line_height: Number(e.target.value) })} className={`${cell} w-20`} /></label>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={!!busy} data-testid="template-edit-save"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-stone-950 text-[#dddd16] text-xs font-bold uppercase tracking-wider hover:bg-stone-800 disabled:opacity-50 transition-colors">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
          </button>
          <button type="button" onClick={runPreview} disabled={!!busy} data-testid="template-sample-preview"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-stone-300 text-stone-800 text-xs font-bold uppercase tracking-wider hover:border-stone-950 disabled:opacity-50 transition-colors">
            {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Preview with sample text
          </button>
        </div>
      </div>
      <div className="max-w-md">
        <PosterPreview image={preview?.image} layout={layout} guides fits={preview?.fits} testid="template-preview" />
        {preview && !preview.ok && <p className="text-xs text-red-700 mt-2">{preview.fits.find((f) => !f.ok)?.message}</p>}
        {preview && (
          <p className="text-[11px] text-stone-600 mt-2" data-testid="template-preview-wording">
            Description previewed: {preview.wording ? <span className="font-semibold text-stone-900">&ldquo;{preview.wording}&rdquo;</span> : <span className="italic">none (blank, so no text is added)</span>}
          </p>
        )}
        {preview && previewedKey !== currentKey && (
          <p className="text-[11px] text-amber-800 mt-1" data-testid="template-preview-stale">You&apos;ve made changes since this preview. Click Preview with sample text to update it.</p>
        )}
        <p className="text-[11px] text-stone-500 mt-2">Dashed rectangles show the text areas on screen only. They never appear in downloads.</p>
      </div>
    </div>
  );
}
